import { Badge } from "@ocr-monorepo/ui/components/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@ocr-monorepo/ui/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ocr-monorepo/ui/components/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@ocr-monorepo/ui/components/accordion";
import type { CreateOpenAPIPageOptions } from "fumadocs-openapi/ui";
import { createElement, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

type ContentOptions = NonNullable<CreateOpenAPIPageOptions["content"]>;
type RenderPageLayout = NonNullable<ContentOptions["renderPageLayout"]>;
type RenderOperationLayout = NonNullable<ContentOptions["renderOperationLayout"]>;
type RenderWebhookLayout = NonNullable<ContentOptions["renderWebhookLayout"]>;
type RenderRequestTabs = NonNullable<ContentOptions["renderRequestTabs"]>;
type RenderResponseTabs = NonNullable<ContentOptions["renderResponseTabs"]>;
type RenderAPIExampleLayout = NonNullable<ContentOptions["renderAPIExampleLayout"]>;

type PageLayoutSlots = Parameters<RenderPageLayout>[0];
type OperationLayoutSlots = Parameters<RenderOperationLayout>[0];
type WebhookLayoutSlots = Parameters<RenderWebhookLayout>[0];
type RequestTabsOptions = Parameters<RenderRequestTabs>[0];
type RequestExampleItem = RequestTabsOptions["items"][number];
type RequestRenderContext = Parameters<RenderRequestTabs>[1];
type ResponseTabsOptions = Parameters<RenderResponseTabs>[0];
type ResponseTab = ResponseTabsOptions["tabs"][number];
type ResponseRenderContext = Parameters<RenderResponseTabs>[1];
type APIExampleLayoutSlots = Parameters<RenderAPIExampleLayout>[0];
const API_CARD_CLASS = "rounded-lg border-border/70 shadow-sm";
const API_CODE_PANEL_CLASS = "rounded-md bg-muted";

function stringifyCode(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === undefined) return "";

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function hasContent(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return true;
}

function renderPayloadAccordion(item: RequestExampleItem) {
  const requestData = item.data as unknown as Record<string, unknown>;
  const bodyLabel = requestData.bodyMediaType
    ? `Body (${String(requestData.bodyMediaType)})`
    : "Body";
  const sections = [
    { key: "path", label: "Path Parameters" },
    { key: "query", label: "Query Parameters" },
    { key: "header", label: "Headers" },
    { key: "cookie", label: "Cookies" },
    { key: "body", label: bodyLabel },
  ];
  const visibleSections = sections.filter((section) =>
    hasContent(requestData[section.key]),
  );

  if (visibleSections.length === 0) return null;

  return (
    <Accordion type="multiple" className="w-full">
      {visibleSections.map((section) => (
        <AccordionItem key={section.key} value={section.key}>
          <AccordionTrigger className="text-sm font-medium">
            {section.label}
          </AccordionTrigger>
          <AccordionContent>
            <pre className={`overflow-x-auto p-3 text-xs ${API_CODE_PANEL_CLASS}`}>
              <code>{stringifyCode(requestData[section.key])}</code>
            </pre>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

const renderPageLayout: RenderPageLayout = async (slots: PageLayoutSlots) => {
  const hasOperations = Boolean(slots.operations && slots.operations.length > 0);
  const hasWebhooks = Boolean(slots.webhooks && slots.webhooks.length > 0);

  return (
    <div className="space-y-10 text-sm">
      {hasOperations ? (
        <section className="space-y-8">
          {slots.operations?.map((operation, index) => (
            <Card
              key={`${operation.item.path}:${operation.item.method}:${index}`}
              className={API_CARD_CLASS}
            >
              <CardContent className="pt-6">{operation.children}</CardContent>
            </Card>
          ))}
        </section>
      ) : null}

      {hasWebhooks ? (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold tracking-tight">Webhooks</h2>
          <div className="space-y-8">
            {slots.webhooks?.map((webhook, index) => (
              <Card
                key={`${webhook.item.name}:${webhook.item.method}:${index}`}
                className={API_CARD_CLASS}
              >
                <CardContent className="pt-6">{webhook.children}</CardContent>
              </Card>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

const renderOperationLayout: RenderOperationLayout = async (
  slots: OperationLayoutSlots,
) => {
  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem] 2xl:grid-cols-[minmax(0,1fr)_38rem]">
      <div className="space-y-6">
        {slots.header}
        {slots.apiPlayground}
        {slots.description}
        {slots.authSchemes}
        {slots.parameters}
        {slots.body}
        {slots.responses}
        {slots.callbacks}
      </div>
      <aside className="space-y-6 xl:sticky xl:top-8 xl:self-start">
        {slots.apiExample}
      </aside>
    </div>
  );
};

const renderWebhookLayout: RenderWebhookLayout = async (
  slots: WebhookLayoutSlots,
) => {
  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_28rem] 2xl:grid-cols-[minmax(0,1fr)_34rem]">
      <div className="space-y-6">
        {slots.header}
        {slots.description}
        {slots.authSchemes}
        {slots.parameters}
        {slots.body}
        {slots.responses}
        {slots.callbacks}
      </div>
      <aside className="space-y-6 xl:sticky xl:top-8 xl:self-start">
        {slots.requests}
      </aside>
    </div>
  );
};

const renderRequestTabs: RenderRequestTabs = async (
  { items, route }: RequestTabsOptions,
  ctx: RequestRenderContext,
) => {
  if (items.length === 0) {
    return (
      <Card className={API_CARD_CLASS}>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Example Requests</CardTitle>
        </CardHeader>
        <CardContent className="pb-6 pt-0 text-sm text-muted-foreground">
          No request examples available.
        </CardContent>
      </Card>
    );
  }

  function renderItem(item: RequestExampleItem) {
    const requestData = item.data as unknown as Record<string, unknown>;
    const methodLabel = String(requestData.method ?? "request");

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="uppercase">
            {methodLabel}
          </Badge>
          <code className="text-xs text-muted-foreground">{route}</code>
        </div>
        {item.description ? (
          <div className="prose prose-sm max-w-none">
            {ctx.renderMarkdown?.(item.description) ??
              ctx._default_processMarkdown(item.description)}
          </div>
        ) : null}
        {renderPayloadAccordion(item)}
      </div>
    );
  }

  return (
    <Card className={API_CARD_CLASS}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Example Requests</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {items.length > 1 ? (
          <Tabs defaultValue={items[0].id} className="w-full">
            <TabsList className="h-auto flex-wrap">
              {items.map((item) => (
                <TabsTrigger key={item.id} value={item.id}>
                  {item.name}
                </TabsTrigger>
              ))}
            </TabsList>
            {items.map((item) => (
              <TabsContent key={item.id} value={item.id} className="pt-2">
                {renderItem(item)}
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          renderItem(items[0])
        )}
      </CardContent>
    </Card>
  );
};

const renderResponseTabs: RenderResponseTabs = async (
  { tabs }: ResponseTabsOptions,
  ctx: ResponseRenderContext,
) => {
  if (tabs.length === 0) return null;

  const tabItems = tabs.map((tab, index) => ({
    id: `${tab.code}-${index}`,
    tab,
  }));

  function renderResponseExample(example: { sample: unknown; description?: string }) {
    return (
      <div className="space-y-3">
        {example.description ? (
          <div className="prose prose-sm max-w-none">
            {ctx.renderMarkdown?.(example.description) ??
              ctx._default_processMarkdown(example.description)}
          </div>
        ) : null}
        <pre className={`overflow-x-auto p-3 text-xs ${API_CODE_PANEL_CLASS}`}>
          <code>{stringifyCode(example.sample)}</code>
        </pre>
      </div>
    );
  }

  return (
    <Tabs defaultValue={tabItems[0].id} className="w-full">
      <TabsList className="h-auto flex-wrap">
        {tabItems.map((item) => (
          <TabsTrigger key={item.id} value={item.id}>
            {item.tab.code}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabItems.map((item) => {
        const examples = item.tab.examples ?? [];

        return (
          <TabsContent key={item.id} value={item.id} className="space-y-4 pt-3">
            {item.tab.mediaType ? (
              <Badge variant="secondary">{item.tab.mediaType}</Badge>
            ) : null}

            {examples.length > 1 ? (
              <Accordion
                type="single"
                collapsible
                defaultValue={String(examples[0]?.label ?? "")}
                className="w-full"
              >
                {examples.map((example) => (
                  <AccordionItem
                    key={String(example.label)}
                    value={String(example.label)}
                  >
                    <AccordionTrigger className="text-sm font-medium">
                      {example.label}
                    </AccordionTrigger>
                    <AccordionContent>
                      {renderResponseExample(example)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : examples.length === 1 ? (
              renderResponseExample(examples[0])
            ) : (
              <p className="text-sm text-muted-foreground">Empty response body.</p>
            )}
          </TabsContent>
        );
      })}
    </Tabs>
  );
};

const renderAPIExampleLayout: RenderAPIExampleLayout = async (
  slots: APIExampleLayoutSlots,
) => {
  return (
    <div className="space-y-4">
      <Card className={API_CARD_CLASS}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Request Examples</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          {slots.selector}
          {slots.usageTabs}
        </CardContent>
      </Card>
      <Card className={API_CARD_CLASS}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Response Samples</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">{slots.responseTabs}</CardContent>
      </Card>
    </div>
  );
};

const headingClasses: Record<number, string> = {
  1: "scroll-m-20 text-3xl font-semibold tracking-tight",
  2: "scroll-m-20 text-2xl font-semibold tracking-tight",
  3: "scroll-m-20 text-xl font-semibold tracking-tight",
  4: "scroll-m-20 text-lg font-semibold tracking-tight",
  5: "scroll-m-20 text-base font-semibold tracking-tight",
  6: "scroll-m-20 text-base font-medium tracking-tight",
};

const renderHeading: NonNullable<CreateOpenAPIPageOptions["renderHeading"]> = (
  props: HTMLAttributes<HTMLHeadingElement>,
  depth: number,
) => {
  const clampedDepth = Math.max(1, Math.min(6, depth));
  const tag = `h${clampedDepth}`;

  return createElement(
    tag,
    {
      ...props,
      className: cn(headingClasses[clampedDepth], props.className),
    },
    props.children,
  );
};

const renderCodeBlock: NonNullable<CreateOpenAPIPageOptions["renderCodeBlock"]> = ({
  lang,
  code,
}) => {
  return (
    <Card className={API_CARD_CLASS}>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
          {lang}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <pre className={`overflow-x-auto p-3 text-xs ${API_CODE_PANEL_CLASS}`}>
          <code>{code}</code>
        </pre>
      </CardContent>
    </Card>
  );
};

export const apiPageRenderers: Pick<
  CreateOpenAPIPageOptions,
  "content" | "renderHeading" | "renderCodeBlock"
> = {
  content: {
    renderPageLayout,
    renderOperationLayout,
    renderWebhookLayout,
    renderRequestTabs,
    renderResponseTabs,
    renderAPIExampleLayout,
  },
  renderHeading,
  renderCodeBlock,
};
